<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractInvoicing',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelCols = ref(6);
  const ux = ref([]);

  const $data = computed(() => ({
    labelCols: labelCols.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'labelCols' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { labelCols, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const item = computed(() => {
    return props.contract.invoicing;
  });
  const billingCycleName = computed(() => {
    return item.value.billing_cycle.split(' ')[1];
  });
  const billingCycleValue = computed(() => {
    return item.value.billing_cycle.split(' ')[0];
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_2095089031469157_emit_09p3b1hqo_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_4ly95obp2_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_j62drzyac_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractEditor',
      props: {
        target: 'invoicing',
        contract: _.cloneDeep(props.contract),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_q3m8qdf28 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_sb0alasj4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_q3m8qdf28,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_sb0alasj4],
    event: 'reload',
  });
  const act_2095089031469157_emit_09p3b1hqo = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_09p3b1hqo_getActionArgs,
  });
  const inst_evh_2095089031469157_close_4ly95obp2 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_09p3b1hqo,
      event: 'close',
      when: inst_evh_2095089031469157_close_4ly95obp2_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_j62drzyac =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_j62drzyac_getActionArgs,
      events: [inst_evh_2095089031469157_close_4ly95obp2],
    });
  const inst_evh_2095089031469157_clickPreventStop_94ss1wrbl =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_j62drzyac,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_94ss1wrbl],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_sb0alasj4,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_4ly95obp2,
    inst_evh_2095089031469157_clickPreventStop_94ss1wrbl,
    inst_evh_2095089031469157_clickPreventStop,
  };

  const evh_7315092382398562_reload_sb0alasj4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_sb0alasj4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_4ly95obp2 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_4ly95obp2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_94ss1wrbl = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_94ss1wrbl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Contract', 'Financial') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup v-if="loaded">
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Advances')"
          :labelCols="labelCols"
        >
          <ItpText>
            {{ item.rental_fee_advances_initial_months }}
            {{ pgettext('ContractAdvances', 'months initial') }}
            <br />
            {{ item.rental_fee_advances_reserve_months }}
            {{ pgettext('ContractAdvances', 'months reserved') }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Billing currency')"
          :labelCols="labelCols"
        >
          <ItpText>
            {{ item.billing_currency }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Billing frequency')"
          :labelCols="labelCols"
        >
          <ItpText>
            {{ billingCycleValue }} {{ gettext(billingCycleName) }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Notes on invoice')"
          :labelCols="labelCols"
          :helpTooltip="
            pgettext(
              'Contract',
              'Text to append to the invoices generated from this contract.'
            )
          "
          v-if="item.invoice_notes"
        >
          <ItpText>
            {{ item.invoice_notes }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Payment terms')"
          :labelCols="labelCols"
          v-if="item.payment_terms"
        >
          <ItpText>
            {{ pgettext('ContractPaymentTerms', item.payment_terms.type) }},
            {{ item.payment_terms.value }}
          </ItpText>
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
